import Icon from '@ant-design/icons';
import { App, Input, Popover, message } from 'antd';
import classNames from 'classnames';
import { useCallback, useRef, useState } from 'react';

import ChatSvg from '@/components/Icon/svgs/chat.svg';
import DeleteSvg from '@/components/Icon/svgs/delete.svg';
import EditSvg from '@/components/Icon/svgs/edit.svg';
import MoreSvg from '@/components/Icon/svgs/more.svg';
import MindverseModal, { SizeType } from '@/components/modal';
import { LAYOUT_SIZE } from '@/hooks/useLayoutSize';
import useStorage from '@/hooks/useStorage';
import { useTranslation } from '@/i18n/client';
import { deleteTask, updateTaskName } from '@/service/task';
import { type TaskData } from '@/types/task';
import { EVENT } from '@/utils/event';
import { isExceedContentLimit } from '@/utils/utils';

import styles from './index.less';

interface IProps {
  task: TaskData;
  className?: string;
  mindId: string;
  current?: boolean;
  onDelete?: (taskId: number) => void;
}

const TaskItem = (props: IProps) => {
  const { task, className, current = false, mindId, onDelete: handleDelete } = props;
  const { modal } = App.useApp();

  const originNameRef = useRef<string>(task.title);
  const { t } = useTranslation();

  const [edit, setEdit] = useState<boolean>(false);
  const [editName, setEditName] = useState<string>(task.title);
  const [taskName, setTaskName] = useState<string>(task.title);
  const [loading, setLoading] = useState<boolean>(false);

  const [layoutSize] = useStorage('layoutSize');

  const runDelete = (data: { id?: number; mindId: string; sessionId: string }) => {
    deleteTask(data)
      .then((res) => {
        if (res.data.code === 0) {
          message.success(res.data.message || t('common.deleteSuccess'));
          handleDelete?.(task.id);
        } else {
          throw new Error(res.data.message || t('common.deleteFail'));
        }
      })
      .catch((err) => {
        message.error(err.message || t('common.deleteFail'));
      });
  };

  const handleConfirmDelete = () => {
    modal.confirm({
      title: t('common.sureToDelete'),
      onOk: () => {
        runDelete({ id: task.id, mindId, sessionId: task.sessionId });

        if (current) {
          dispatchEvent(new Event(EVENT.CLICK_NEW_CHAT_BUTTON));
        }
      },
      okText: t('common.delete'),
      cancelText: t('common.cancel')
    });
  };

  const handleSaveName = () => {
    if (editName === originNameRef.current) {
      return;
    }

    setLoading(true);
    updateTaskName({ sessionId: task.sessionId, userTitle: editName })
      .then((res) => {
        if (res.data.code === 0) {
          message.success(res.data.message || 'Successfully updated.');
          originNameRef.current = editName;
          dispatchEvent(
            new CustomEvent(EVENT.SET_CURRENT_TASK_NAME, {
              detail: {
                sessionId: task.sessionId,
                title: editName
              }
            })
          );
          setEdit(false);
          setTaskName(editName);
        } else {
          message.error(res.data.message || 'Failed to update.');
        }
      })
      .catch((err) => {
        message.error(err || 'Failed to update.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const renderPopoverContent = useCallback(() => {
    return (
      <div
        className={styles.popoverContentContainer}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div
          className={classNames(styles.itemContainer, { [styles.canHover]: true })}
          onClick={() => {
            setEdit(true);
          }}
        >
          <Icon className={styles.icon} component={EditSvg} />
          <span>{t('common.edit')}</span>
        </div>

        <div
          className={classNames(styles.itemContainer, { [styles.canHover]: true })}
          onClick={handleConfirmDelete}
        >
          <Icon className={styles.icon} component={DeleteSvg} />
          <span>{t('common.delete')}</span>
        </div>
      </div>
    );
  }, []);

  return (
    <>
      <div
        className={classNames(styles.container, className, 'group', {
          [styles.current]: current
        })}
        onClick={() => {
          if (!current) {
            dispatchEvent(new CustomEvent(EVENT.CONTINUE_CHAT, { detail: task.sessionId }));
          }
        }}
      >
        {current ? <Icon className="mr-2 text-primary text-[18px]" component={ChatSvg} /> : null}

        <div className={styles.title} title={task.title}>
          {taskName}
        </div>

        <Popover
          arrow={false}
          content={renderPopoverContent()}
          overlayClassName={styles.overlayClassName}
          placement="bottomLeft"
          trigger={['hover']}
        >
          <Icon
            className={classNames(
              '!ml-auto box-border w-6 h-6 p-[3px] text-primary-black text-base border border-solid border-[#eee9e4] shadow-[0_4px_4px_0_rgba(0,0,0,0.08)] bg-primary-white rounded-[30px] transition-opacity group-hover:opacity-100',
              layoutSize === LAYOUT_SIZE.SMALL ? 'opacity-100' : 'opacity-0'
            )}
            component={MoreSvg}
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
        </Popover>
      </div>
      <MindverseModal
        destroyOnClose
        okButtonProps={{ loading, disabled: !editName.trim() }}
        onCancel={() => {
          setEdit(false);
        }}
        onOk={handleSaveName}
        open={edit}
        size={SizeType.normal}
        title={'Edit'}
      >
        <Input
          autoFocus={true}
          maxLength={100}
          onChange={(e) => {
            if (isExceedContentLimit(e.target.value, 100)) {
              return;
            }

            setEditName(e.target.value);
          }}
          type="text"
          value={editName}
        />
      </MindverseModal>
    </>
  );
};

export default TaskItem;
